import React from "react";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import {
  alpha,
  AppBar,
  Avatar,
  Badge,
  Box,
  InputBase,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Typography,
} from "@mui/material";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import AuthContext from "../../store/auth-context";
import { useNavigate } from "react-router-dom";
import { resolveImageUrl, unwrapList, getErrorMessage } from "../../services/apiUtils";
import NotificationService, { INotification, isNotificationRead, getUnreadCount } from "../../services/NotificationService";
import TopLocaleBar from "../common/TopLocaleBar";
import { useTranslation } from "react-i18next";
const drawerWidth = 268;
const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  minHeight: 72,
  paddingLeft: 32,
  paddingRight: 32,
});

const Icons = styled(Box)(({ theme }) => ({
  display: "none",
  alignItems: "center",
  gap: "16px",
  marginLeft: "auto",
  [theme.breakpoints.up("sm")]: {
    display: "flex",
  },
}));
const UserBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  [theme.breakpoints.up("sm")]: {
    display: "none",
  },
}));
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,

  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),

    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "50ch",
      "&:focus": {
        width: "55ch",
      },
    },
  },
}));

const StyledLogo = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  padding: "4px 12px 4px 4px",
  borderRadius: "999px",
  marginLeft: "4px",
  transition: "all .15s ease",
  boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    cursor: "pointer",
  },
}));

type ButtonProps = {
  onHandleDrawerToggle: (params: any) => any;
};
const Header = ({ onHandleDrawerToggle }: ButtonProps) => {
  const { t } = useTranslation();
  const authCtx = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [notifAnchorEl, setNotifAnchorEl] = React.useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchorEl);
  const [notifications, setNotifications] = React.useState<Array<INotification>>([]);
  const [notifLoading, setNotifLoading] = React.useState(false);
  const [notifError, setNotifError] = React.useState<string | null>(null);

  const loadNotifications = React.useCallback(() => {
    if (!authCtx.user?.userId) return;
    setNotifLoading(true);
    setNotifError(null);
    NotificationService.getByUserAndType(authCtx.user.userId, "ALL")
      .then((res: any) => {
        setNotifications(unwrapList<INotification>(res));
      })
      .catch((err: any) => {
        setNotifications([]);
        setNotifError(getErrorMessage(err, "Could not load notifications."));
      })
      .finally(() => setNotifLoading(false));
  }, [authCtx.user?.userId]);

  React.useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
    loadNotifications();
  };
  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const goToNotifications = () => {
    handleNotifClose();
    navigate("/notifications");
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutHandler = () => {
    authCtx.logout();
    navigate("/login", { replace: true });
  };

  const handleNavbarCountryChange = () => {
    authCtx.logout();
    navigate("/login", { replace: true });
  };

  const goToSettings = () => {
    handleClose();
    navigate("/settings");
  };

  const goToProfile = () => {
    handleClose();
    navigate("/profile");
  };

  const user = authCtx.user;
  const avatarSrc = resolveImageUrl(user?.avatarUrl) || undefined;
  const initials = (user?.name || "U").charAt(0).toUpperCase();
  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
      }}
    >
      <StyledToolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onHandleDrawerToggle}
          sx={{ mr: 2, display: { sm: "none" } }}
        >
          <MenuIcon />
        </IconButton>
        <Icons>
          <TopLocaleBar
            color="inherit"
            reloadOnChange
            onCountryChange={handleNavbarCountryChange}
            sx={{ py: 0, gap: 2, justifyContent: "flex-start" }}
          />
          <IconButton
            color="inherit"
            onClick={handleNotifClick}
            aria-label="notifications"
            sx={{
              width: 40,
              height: 40,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "primary.light", color: "primary.main" },
            }}
          >
            <Badge color="error" badgeContent={getUnreadCount(notifications)}>
              <NotificationsNoneOutlinedIcon sx={{ color: "inherit" }} />
            </Badge>
          </IconButton>
          <StyledLogo onClick={handleClick}>
            <Avatar
              sx={{ width: 30, height: 30, mr: 1 }}
              alt={user?.name || "User"}
              src={avatarSrc}
            >
              {initials}
            </Avatar>
            {user?.country && (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  bgcolor: "rgba(0,0,0,0.08)",
                  color: "inherit",
                  borderRadius: "6px",
                  px: "6px",
                  py: "2px",
                  mr: 0.5,
                }}
              >
                {user.country.toUpperCase()}
              </Box>
            )}
            <SettingsOutlinedIcon />
          </StyledLogo>
        </Icons>
        <UserBox onClick={handleClick}>
          <Avatar sx={{ width: 30, height: 30 }} alt={user?.name || "User"} src={avatarSrc}>
            {initials}
          </Avatar>
          <Typography component="span">{user?.name || "User"}</Typography>
          {user?.country && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                bgcolor: "rgba(0,0,0,0.08)",
                borderRadius: "6px",
                px: "6px",
                py: "2px",
              }}
            >
              {user.country.toUpperCase()}
            </Box>
          )}
        </UserBox>
      </StyledToolbar>

      <Menu
        anchorEl={notifAnchorEl}
        open={notifOpen}
        onClose={handleNotifClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 340, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("header.notifications")}
          </Typography>
        </Box>
        <Divider />
        {notifLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {!notifLoading && notifError && (
          <MenuItem disabled sx={{ whiteSpace: "normal" }}>
            <ListItemText primary={notifError} />
          </MenuItem>
        )}
        {!notifLoading && !notifError && notifications.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={t("header.noNotifications")} />
          </MenuItem>
        )}
        {!notifLoading &&
          !notifError &&
          notifications.slice(0, 20).map((n, idx) => {
            const unread = !isNotificationRead(n);
            return (
              <MenuItem
                key={n.notificationId ?? n.id ?? idx}
                onClick={goToNotifications}
                sx={{ whiteSpace: "normal", alignItems: "flex-start", gap: 1 }}
              >
                <Box
                  sx={{
                    mt: "6px",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor: unread ? "error.main" : "transparent",
                  }}
                />
                <ListItemText
                  primary={n.title || n.message || n.body || "Notification"}
                  secondary={n.title ? n.message || n.body || n.description : n.description}
                  primaryTypographyProps={{ fontWeight: unread ? 700 : 400 }}
                />
              </MenuItem>
            );
          })}
        <Divider />
        <MenuItem onClick={goToNotifications} sx={{ justifyContent: "center", fontWeight: 600 }}>
          {t("header.viewAll")}
        </MenuItem>
      </Menu>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 1, minWidth: 240 } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40 }} alt={user?.name || "User"} src={avatarSrc}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {user?.name || "User"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {user?.email || ""}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={goToProfile} sx={{ gap: 1.5, py: 1 }}>
          <PersonOutlineRoundedIcon fontSize="small" />
          {t("header.profile")}
        </MenuItem>
        <MenuItem onClick={goToSettings} sx={{ gap: 1.5, py: 1 }}>
          <SettingsOutlinedIcon fontSize="small" />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={logoutHandler} sx={{ gap: 1.5, py: 1, color: "error.main" }}>
          <LogoutRoundedIcon fontSize="small" />
          {t("header.logout")}
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Header;
